import { useStableCallback } from "@prismicio/editor-support/React"
import React from "react"

import { Icon, type IconName } from "../Icon"
import { IconButton } from "../IconButton"
import { Popover } from "../Popover"
import { ProgressCircle } from "../ProgressCircle"
import { Text } from "../Text"
import styles from "./Toast.module.css"

type Props = {
  anchor: React.ReactNode
  open: boolean
  onOpenChange?: (open: boolean) => void
  icon?: IconName
  loadingIndicator?: boolean
  title: string
  seconds?: number
  action?: {
    title: string
    onClick: () => void
  }
  cancel?: {
    onClick: () => void
  }
}

/**
 * We don't use the radix ui toast because of a bug with the timeout.
 * Radix's Toast task tracker is here: https://github.com/radix-ui/primitives/issues/1171
 */
export const Toast = ({
  anchor,
  open,
  onOpenChange,
  icon,
  loadingIndicator,
  title,
  seconds,
  action,
  cancel
}: Props) => {
  const stableOnOpenChange = useStableCallback(onOpenChange)
  React.useEffect(() => {
    if (!open || !seconds) return
    const timeout = setTimeout(() => {
      stableOnOpenChange(false)
    }, seconds * 1000)
    return () => clearTimeout(timeout)
  }, [seconds, open, stableOnOpenChange])

  const hasActions = action || cancel

  return (
    <Popover
      anchor={anchor}
      open={open}
      onOpenChange={onOpenChange}
      onInteractOutside={(event) => event.preventDefault()}
      onEscapeKeyDown={(event) => {
        // Prevent it from closing with `esc` if there is an action.
        if (action) event.preventDefault()
      }}
      // We don't want to focus the portal when the toast opens.
      focusOnEnter={false}
    >
      <div className={styles.root}>
        <div className={styles.content}>
          {icon && <Icon name={icon} color="grey11" size="medium" />}
          {loadingIndicator && <ProgressCircle color="dark12" />}
          <Text variant="normal" color="white">
            {title}
          </Text>
        </div>
        {hasActions && (
          <>
            <div className={styles.separator} />
            {action && (
              <button
                className={styles.button}
                onClick={() => {
                  onOpenChange?.(false)
                  action.onClick()
                }}
              >
                {action.title}
              </button>
            )}
            {cancel && (
              <IconButton
                icon="close"
                size="medium"
                hasPadding={false}
                onClick={cancel.onClick}
              />
            )}
          </>
        )}
      </div>
    </Popover>
  )
}
